<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getWebpUrl } from '@/utils/webpAssets'
import { useRouter } from 'vue-router'
import iconBank from '@/assets/svg/footer_icon_bank.svg?url'
import ExploreSection from '@/views/home/components/ExploreSection.vue'
import { useConstConfigV2 } from '@/composables/useConstConfigV2'
import RichText from '@/components/RichText'
import { decodeHtml } from '@/utils/decodeHtml'
import { useGameCategory } from '@/composables/useGameCategory'
import { getGameCategoryTarget } from '@/utils/gameCategoryRoute'
import { ABOUT_TABS } from '@/views/about/content/tabs'
interface FooterLinkItem {
  url: string
  name: string
}

interface FooterLogoGroup {
  title: string
  img?: string
  logos?: string[]
}
const { locale, t } = useI18n()

const richText = ref<string | undefined>()
const logoList = ref<{ pic?: string; url?: string; name?: string }[]>([])
const { category } = useGameCategory()

const { getHomePageContacts, getHomePageRichText } = useConstConfigV2()
const providerLogoList = [
  getWebpUrl('imgs/footer/provider/Askmeslot.webp'),
  getWebpUrl('imgs/footer/provider/Booongo.webp'),
  getWebpUrl('imgs/footer/provider/CMD368.webp'),
  getWebpUrl('imgs/footer/provider/CQ9.webp'),
  getWebpUrl('imgs/footer/provider/FTG.webp'),
  getWebpUrl('imgs/footer/provider/Fachai.webp'),
  getWebpUrl('imgs/footer/provider/JDB.webp'),
  getWebpUrl('imgs/footer/provider/JILI.webp'),
  getWebpUrl('imgs/footer/provider/KaGaming.webp'),
  getWebpUrl('imgs/footer/provider/Kingmidas.webp'),
  getWebpUrl('imgs/footer/provider/MWplay.webp'),
  getWebpUrl('imgs/footer/provider/Nextspin.webp'),
  getWebpUrl('imgs/footer/provider/Pragmaticplay.webp'),
  getWebpUrl('imgs/footer/provider/RICH88.webp'),
  getWebpUrl('imgs/footer/provider/SABASport.webp'),
  getWebpUrl('imgs/footer/provider/SBobet.webp'),
  getWebpUrl('imgs/footer/provider/SaGaming.webp'),
  getWebpUrl('imgs/footer/provider/TopPlayer.webp'),
  getWebpUrl('imgs/footer/provider/VA.webp'),
  getWebpUrl('imgs/footer/provider/biggaming.webp'),
  getWebpUrl('imgs/footer/provider/dreamGaming.webp'),
  getWebpUrl('imgs/footer/provider/evolution.webp'),
  getWebpUrl('imgs/footer/provider/inplaymatrix.webp'),
  getWebpUrl('imgs/footer/provider/microgaming.webp'),
  getWebpUrl('imgs/footer/provider/playace.webp'),
  getWebpUrl('imgs/footer/provider/playtech.webp'),
  getWebpUrl('imgs/footer/provider/sexybaccarat.webp')
]
const paymentMethods = [
  { img: iconBank },
  { img: getWebpUrl('imgs/footer/figma/fpx.webp') },
  { img: getWebpUrl('imgs/footer/figma/usdt.webp') },
  { img: getWebpUrl('imgs/footer/figma/touch_n_go.webp') },
  { img: getWebpUrl('imgs/footer/figma/duit_now.webp') },
  { img: getWebpUrl('imgs/footer/figma/gx_bank.webp') },
  { img: getWebpUrl('imgs/footer/figma/ryt_bank.webp') }
]

onMounted(async () => {
  richText.value = await getHomePageRichText(locale.value)
  logoList.value = await getHomePageContacts()
})

watch(
  () => locale.value,
  async () => {
    richText.value = await getHomePageRichText(locale.value)
  }
)
const entertainmentLinks = computed(() => ({
  title: t('Entertainment'),
  children: category.value.map((item) => ({
    name: item.label ?? '',
    url: getGameCategoryTarget(item)
  }))
}))

const informationLinkColumns = computed<FooterLinkItem[][]>(() => [
  ABOUT_TABS.map((item) => ({
    name: t(item.labelKey),
    url: `/about?tab=${item.key}`
  }))
])

const providerGroups = computed<FooterLogoGroup[]>(() => [
  {
    title: t('Strategic Partner Provider'),
    img: getWebpUrl('imgs/footer/our_exclusive_logo.webp')
  },
  {
    title: t('Casino Partner Provider'),
    img: getWebpUrl('imgs/footer/casino_partner_provider.webp')
  },
  {
    title: t('Other Partnership Provider'),
    logos: providerLogoList
  }
])

const copyrightText = computed(() => t('Copyright @2026 NoMoreBet. All rights reserved.'))
const router = useRouter()

const openRoute = (url: string) => {
  router.push(url)
}

const sectionLabels = computed(() => ({
  informationLinks: t('Information Links'),
  gamingLicense: t('Gaming License'),
  strategicPartner: t('Strategic Partner Provider'),
  casinoPartner: t('Casino Partner Provider'),
  otherPartner: t('Other Partnership Provider'),
  paymentMethod: t('支付方式')
}))
</script>

<template>
  <footer class="site-footer">
    <!-- <ExploreSection /> -->
    <div class="site-footer__inner">
      <section class="top-block">
        <div class="brand-panel">
          <img
            class="brand-logo"
            src="@/assets/imgs/sideMenu/logo.webp"
            alt="NoMoreBet"
            loading="lazy" />
          <p class="brand-desc">
            <RichText :content="decodeHtml(richText)" />
          </p>

          <div class="license-panel">
            <h4>{{ sectionLabels.gamingLicense }}</h4>
            <img
              class="license-logo"
              src="@/assets/imgs/footer/figma/gaming_license.webp"
              :alt="sectionLabels.gamingLicense"
              loading="lazy" />
          </div>
        </div>

        <div class="links-panel">
          <div class="link-group entertainment-group">
            <h4>{{ entertainmentLinks.title }}</h4>
            <button
              v-for="item in entertainmentLinks.children"
              :key="item.name"
              class="link-item"
              type="button"
              @click="openRoute(item.url)">
              {{ item.name }}
            </button>
          </div>

          <div class="link-group information-group">
            <h4>{{ sectionLabels.informationLinks }}</h4>

            <div class="information-columns">
              <div
                v-for="(column, colIndex) in informationLinkColumns"
                :key="colIndex"
                class="column">
                <button
                  v-for="item in column"
                  :key="item.name"
                  class="link-item"
                  type="button"
                  @click="openRoute(item.url)">
                  {{ item.name }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="split-line" />

      <section class="provider-block">
        <div v-for="group in providerGroups" :key="group.title" class="provider-group">
          <h4>{{ group.title }}</h4>
          <div v-if="group.logos?.length" class="provider-marquee">
            <div class="provider-marquee__track">
              <img
                v-for="logo in group.logos"
                :key="logo"
                :src="logo"
                :alt="group.title"
                loading="lazy" />
            </div>
          </div>
          <div v-else-if="group.img" class="provider-logos">
            <img class="provider-logo" :src="group.img" :alt="group.title" loading="lazy" />
          </div>
        </div>
      </section>

      <div class="split-line" />

      <section class="bottom-block">
        <div class="bottom-group">
          <h4>{{ sectionLabels.paymentMethod }}</h4>
          <div class="icon-row">
            <img
              v-for="(method, index) in paymentMethods"
              :key="index"
              class="chip-icon"
              :src="method.img"
              alt="Payment Method"
              loading="lazy" />
          </div>
        </div>
      </section>

      <div class="split-line" />

      <section class="kyc-block">
        <img
          class="kyc-logo"
          src="@/assets/imgs/footer/btm_kyc.webp"
          alt="KYC Policy"
          loading="lazy" />
        <p class="kyc-text">{{ t('kyc_txt') }}</p>
      </section>

      <div class="split-line" />

      <div class="copyright">{{ copyrightText }}</div>
    </div>
  </footer>
</template>

<style scoped lang="scss">
.site-footer {
  margin-top: 48px;
  background: #0f0f0f;
  padding: 30px clamp(16px, 3vw, 42px) 20px;

  &__inner {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 40px;
  }
}

.top-block {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(360px, 1.25fr);
  gap: clamp(24px, 4vw, 50px);
}

.brand-panel {
  display: flex;
  flex-direction: column;
  gap: 22px;

  .brand-logo {
    width: 112px;
    height: auto;
  }

  .brand-desc {
    margin: 0;
    color: #fff;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.55;
    max-width: 470px;
  }
}

.license-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;

  h4 {
    margin: 0;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.15;
  }

  .license-logo {
    width: 120px;
    height: 28px;
    object-fit: contain;
  }
}

.links-panel {
  display: grid;
  grid-template-columns: minmax(120px, 180px) minmax(220px, 1fr);
  gap: clamp(16px, 2vw, 26px);

  .information-group {
    border-left: 1px solid rgba(255, 255, 255, 0.12);
    padding-left: clamp(16px, 2vw, 24px);
  }
}

.link-group {
  h4 {
    margin: 0 0 16px;
    color: #e8ba6e;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.2;
  }
}

.information-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(120px, 1fr));
  gap: 18px;
}

.column,
.entertainment-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.link-item {
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
  color: #fff;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.3;
  cursor: pointer;

  &:hover {
    color: #e8ba6e;
  }
}

.split-line {
  width: 100%;
  height: 1px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.2) 51.07%,
    rgba(255, 255, 255, 0.05) 100.16%
  );
}

.provider-block,
.bottom-block {
  display: grid;
  grid-template-columns: repeat(3, minmax(220px, 1fr));
  gap: clamp(18px, 2.4vw, 30px);
}

.bottom-block {
  grid-template-columns: repeat(2, minmax(240px, 1fr));
}

.provider-group,
.bottom-group {
  display: flex;
  flex-direction: column;
  gap: 12px;

  h4 {
    margin: 0;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;
  }
}

.provider-logos,
.icon-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.provider-logo {
  height: 30px;
  width: auto;

  object-fit: contain;
}

.provider-marquee {
  width: 100%;
  overflow: hidden;

  &__track {
    display: flex;
    width: max-content;
    gap: 10px;
    animation: provider-marquee 60s linear infinite;
  }

  &:hover .provider-marquee__track {
    animation-play-state: paused;
  }

  img {
    flex: 0 0 130px;
    display: block;
    height: 30px;
    width: 130px;
    object-fit: contain;
  }
}

@keyframes provider-marquee {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-100%);
  }
}

.chip-icon {
  width: 35px;
  height: 35px;
  object-fit: contain;
  border-radius: 4px;
}

.kyc-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.kyc-logo {
  width: min(345px, 100%);
  height: auto;
  object-fit: contain;
}

.kyc-text {
  margin: 0;
  color: #bfbfbf;
  font-size: 12px;
  line-height: 1.45;
}

.copyright {
  padding-top: 2px;
  text-align: center;
  color: #bfbfbf;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  white-space: pre-line;
}

@media (max-width: 1080px) {
  .top-block {
    grid-template-columns: 1fr;
  }

  .provider-block {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }
}

@media (max-width: 768px) {
  .links-panel {
    grid-template-columns: 1fr;

    .information-group {
      border-left: 0;
      border-top: 1px solid rgba(255, 255, 255, 0.12);
      padding-left: 0;
      padding-top: 14px;
    }
  }

  .information-columns {
    grid-template-columns: 1fr;
  }

  .provider-block,
  .bottom-block {
    grid-template-columns: 1fr;
  }
}
</style>
